import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  DataTable,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  TextInput,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { formatDateTime, prettyJson, toneForLifecycle, toneForSideEffect } from '../lib/format';

interface Version {
  id: string;
  version: string;
  state: string;
  mcpExposure: string;
  sideEffectClass: string;
  lastReviewedAt: string | null;
  publishedAt: string | null;
}

interface Target {
  id: string;
  providerAccountId: string;
  providerKey: string;
  accountLabel: string;
  environment: string;
  state: string;
  priority: number;
  weight: number;
  timeoutMs: number;
  maxAttempts: number;
  modelMapping: string | null;
  healthState: string;
  lastFailureReason: string | null;
}

interface Operation {
  id: string;
  operationKey: string;
  serviceKey: string;
  name: string;
  isEnabled: boolean;
  routingStrategy: string;
  cacheEnabled: boolean;
  cacheTtlSeconds: number;
  defaultVersion: { id: string; version: string } | null;
  versions: Version[];
  targets: Target[];
}

export function OperationsPage({ role }: { role: string }) {
  const operations = useResource<{ items: Operation[] }>('/api/admin/operations');
  const accounts = useResource<{
    items: Array<{ id: string; providerKey: string; label: string; environment: string }>;
  }>('/api/admin/provider-accounts');
  const toast = useToast();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [targetFor, setTargetFor] = useState<Operation | null>(null);
  const [targetForm, setTargetForm] = useState({
    providerAccountId: '',
    environment: 'test',
    priority: 100,
    weight: 1,
    modelMapping: '',
  });
  const [versionDetail, setVersionDetail] = useState<string | null>(null);

  const canWrite = role === 'OWNER' || role === 'ADMIN';
  const canOperate = canWrite || role === 'OPERATOR';
  const detail = useResource<Record<string, unknown>>(
    versionDetail ? `/api/admin/operation-versions/${versionDetail}` : null,
  );

  const publish = async (versionId: string) => {
    try {
      await api.post(`/api/admin/operation-versions/${versionId}/review`);
      const result = await api.post<{ catalogVersion: number }>('/api/admin/catalog/publish', {
        operationVersionIds: [versionId],
        makeDefault: true,
      });
      toast.success('Opublikowano', `Wersja katalogu ${result.catalogVersion}`);
      operations.reload();
    } catch (error) {
      const described = describeError(error);
      toast.error(described.title, described.body);
    }
  };

  return (
    <>
      <PageHeader
        title="Operacje"
        description="Konfiguracja wykonawcza (routing, cele, cache) jest oddzielona od kontraktu, który czytają agenci. Publikacja dokumentacji nie zmienia trasy."
      />

      {operations.loading ? (
        <Skeleton rows={6} />
      ) : (operations.data?.items ?? []).length === 0 ? (
        <Card>
          <EmptyState
            title="Brak operacji"
            description="Utwórz operację ręcznie albo zaimportuj dokument OpenAPI, który utworzy wersje robocze."
          />
        </Card>
      ) : (
        (operations.data?.items ?? []).map((operation) => (
          <Card
            key={operation.id}
            title={
              <span className="ah-mono">
                {operation.operationKey}
                {operation.defaultVersion && (
                  <span style={{ color: 'var(--ah-text-subtle)' }}>
                    @{operation.defaultVersion.version}
                  </span>
                )}
              </span>
            }
            description={`${operation.name} · strategia ${operation.routingStrategy}`}
            actions={
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Badge tone={operation.isEnabled ? 'ok' : 'neutral'}>
                  {operation.isEnabled ? 'włączona' : 'wyłączona'}
                </Badge>
                {operation.cacheEnabled && (
                  <Badge tone="info">cache {operation.cacheTtlSeconds}s</Badge>
                )}
                <Button
                  size="sm"
                  onClick={() => setExpanded(expanded === operation.id ? null : operation.id)}
                >
                  {expanded === operation.id ? 'Zwiń' : 'Rozwiń'}
                </Button>
              </div>
            }
            tight
          >
            {expanded === operation.id && (
              <div className="ah-card-body">
                <h3 style={{ fontSize: 13, margin: '0 0 8px' }}>Wersje kontraktu</h3>
                <DataTable
                  rows={operation.versions}
                  rowKey={(row) => row.id}
                  columns={[
                    {
                      key: 'version',
                      header: 'Wersja',
                      render: (row) => <span className="ah-mono">{row.version}</span>,
                    },
                    {
                      key: 'state',
                      header: 'Stan',
                      render: (row) => (
                        <Badge tone={toneForLifecycle(row.state)}>{row.state}</Badge>
                      ),
                    },
                    {
                      key: 'side',
                      header: 'Skutki',
                      render: (row) => (
                        <Badge tone={toneForSideEffect(row.sideEffectClass)}>
                          {row.sideEffectClass}
                        </Badge>
                      ),
                    },
                    {
                      key: 'mcp',
                      header: 'MCP',
                      render: (row) => (
                        <Badge tone={row.mcpExposure === 'DIRECT' ? 'info' : 'neutral'}>
                          {row.mcpExposure}
                        </Badge>
                      ),
                    },
                    {
                      key: 'reviewed',
                      header: 'Przegląd',
                      render: (row) =>
                        row.lastReviewedAt ? (
                          formatDateTime(row.lastReviewedAt)
                        ) : (
                          <Badge tone="warn">brak</Badge>
                        ),
                    },
                    {
                      key: 'published',
                      header: 'Opublikowano',
                      render: (row) => formatDateTime(row.publishedAt),
                    },
                    {
                      key: 'actions',
                      header: '',
                      align: 'right',
                      render: (row) => (
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <Button size="sm" onClick={() => setVersionDetail(row.id)}>
                            Kontrakt
                          </Button>
                          {canWrite && row.state === 'DRAFT' && (
                            <Button size="sm" variant="primary" onClick={() => publish(row.id)}>
                              Przejrzyj i opublikuj
                            </Button>
                          )}
                          {canWrite &&
                            row.state === 'PUBLISHED' &&
                            operation.defaultVersion?.id === row.id && (
                              <Button
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await api.post(
                                      `/api/admin/operation-versions/${row.id}/deprecate`,
                                      {},
                                    );
                                    toast.success('Oznaczono jako przestarzałą');
                                    operations.reload();
                                  } catch (error) {
                                    const described = describeError(error);
                                    toast.error(described.title, described.body);
                                  }
                                }}
                              >
                                Wycofaj
                              </Button>
                            )}
                        </div>
                      ),
                    },
                  ]}
                />

                <h3 style={{ fontSize: 13, margin: '20px 0 8px' }}>Cele routingu</h3>
                <DataTable
                  rows={operation.targets}
                  rowKey={(row) => row.id}
                  empty={
                    <EmptyState
                      title="Brak celów"
                      description="Bez celu operacja zwróci NO_HEALTHY_TARGET."
                    />
                  }
                  columns={[
                    {
                      key: 'account',
                      header: 'Konto',
                      render: (row) => (
                        <span className="ah-mono">
                          {row.providerKey}/{row.accountLabel}
                        </span>
                      ),
                    },
                    { key: 'env', header: 'Środowisko', render: (row) => row.environment },
                    {
                      key: 'priority',
                      header: 'Priorytet',
                      align: 'right',
                      render: (row) => row.priority,
                    },
                    { key: 'weight', header: 'Waga', align: 'right', render: (row) => row.weight },
                    {
                      key: 'model',
                      header: 'Model',
                      render: (row) => <span className="ah-mono">{row.modelMapping ?? '—'}</span>,
                    },
                    {
                      key: 'attempts',
                      header: 'Próby',
                      align: 'right',
                      render: (row) => row.maxAttempts,
                    },
                    {
                      key: 'state',
                      header: 'Stan',
                      render: (row) => (
                        <Badge tone={row.state === 'ENABLED' ? 'ok' : 'warn'}>{row.state}</Badge>
                      ),
                    },
                    {
                      key: 'actions',
                      header: '',
                      align: 'right',
                      render: (row) =>
                        canOperate ? (
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            <Button
                              size="sm"
                              onClick={async () => {
                                try {
                                  await api.patch(`/api/admin/operation-targets/${row.id}`, {
                                    state: row.state === 'ENABLED' ? 'DRAINING' : 'ENABLED',
                                  });
                                  operations.reload();
                                } catch (error) {
                                  const described = describeError(error);
                                  toast.error(described.title, described.body);
                                }
                              }}
                            >
                              {row.state === 'ENABLED' ? 'Wygaszaj' : 'Włącz'}
                            </Button>
                            {canWrite && (
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={async () => {
                                  try {
                                    await api.delete(`/api/admin/operation-targets/${row.id}`);
                                    operations.reload();
                                  } catch (error) {
                                    const described = describeError(error);
                                    toast.error(described.title, described.body);
                                  }
                                }}
                              >
                                Usuń
                              </Button>
                            )}
                          </div>
                        ) : null,
                    },
                  ]}
                />
                {canWrite && (
                  <Button
                    style={{ marginTop: 10 }}
                    onClick={() => {
                      setTargetFor(operation);
                      setTargetForm({
                        providerAccountId: '',
                        environment: 'test',
                        priority: 100,
                        weight: 1,
                        modelMapping: '',
                      });
                    }}
                  >
                    Dodaj cel
                  </Button>
                )}
              </div>
            )}
          </Card>
        ))
      )}

      <Modal
        open={targetFor !== null}
        onOpenChange={(open) => !open && setTargetFor(null)}
        title="Nowy cel routingu"
        description={
          targetFor
            ? `Operacja ${targetFor.operationKey}. Niższy priorytet = wcześniejsza próba.`
            : ''
        }
        footer={
          <>
            <Button onClick={() => setTargetFor(null)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={!targetForm.providerAccountId}
              onClick={async () => {
                if (!targetFor) return;
                try {
                  await api.post(`/api/admin/operations/${targetFor.id}/targets`, {
                    providerAccountId: targetForm.providerAccountId,
                    environment: targetForm.environment,
                    priority: Number(targetForm.priority),
                    weight: Number(targetForm.weight),
                    ...(targetForm.modelMapping ? { modelMapping: targetForm.modelMapping } : {}),
                  });
                  toast.success('Cel dodany');
                  setTargetFor(null);
                  operations.reload();
                } catch (error) {
                  const described = describeError(error);
                  toast.error(described.title, described.body);
                }
              }}
            >
              Dodaj
            </Button>
          </>
        }
      >
        <Field label="Konto dostawcy">
          {(id) => (
            <Select
              id={id}
              value={targetForm.providerAccountId}
              onChange={(event) =>
                setTargetForm({ ...targetForm, providerAccountId: event.target.value })
              }
            >
              <option value="">— wybierz —</option>
              {(accounts.data?.items ?? []).map((account) => (
                <option key={account.id} value={account.id}>
                  {account.providerKey}/{account.label} ({account.environment})
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Środowisko" hint="Musi zgadzać się ze środowiskiem konta.">
          {(id) => (
            <Select
              id={id}
              value={targetForm.environment}
              onChange={(event) =>
                setTargetForm({ ...targetForm, environment: event.target.value })
              }
            >
              <option value="test">test</option>
              <option value="staging">staging</option>
              <option value="production">production</option>
            </Select>
          )}
        </Field>
        <Field label="Priorytet">
          {(id) => (
            <TextInput
              id={id}
              type="number"
              value={targetForm.priority}
              onChange={(event) =>
                setTargetForm({ ...targetForm, priority: Number(event.target.value) })
              }
            />
          )}
        </Field>
        <Field label="Waga" hint="Używana wyłącznie przy strategii WEIGHTED.">
          {(id) => (
            <TextInput
              id={id}
              type="number"
              value={targetForm.weight}
              onChange={(event) =>
                setTargetForm({ ...targetForm, weight: Number(event.target.value) })
              }
            />
          )}
        </Field>
        <Field
          label="Model upstream"
          hint="Dla operacji LLM: nazwa modelu u dostawcy, np. gpt-5-mini."
        >
          {(id) => (
            <TextInput
              id={id}
              value={targetForm.modelMapping}
              onChange={(event) =>
                setTargetForm({ ...targetForm, modelMapping: event.target.value })
              }
            />
          )}
        </Field>
      </Modal>

      <Modal
        open={versionDetail !== null}
        onOpenChange={(open) => !open && setVersionDetail(null)}
        title="Kontrakt operacji"
        description="Dokładnie to czyta agent przez hub_get_operation."
      >
        {detail.loading ? (
          <Skeleton rows={6} />
        ) : (
          <pre className="ah-code">{prettyJson(detail.data)}</pre>
        )}
        <Callout>
          Opublikowana wersja jest niezmienna. Aby zmienić kontrakt, utwórz nową wersję — stara
          pozostanie rozwiązywalna dla klientów, którzy ją przypięli.
        </Callout>
      </Modal>
    </>
  );
}
